import { zodResolver } from '@hookform/resolvers/zod';
import React, { useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  InputField,
  MultiSelectField
} from '@epoch-ui/components/Form';
import RichTextEditor from 'components/common/inputs/RichTextEditor';
import { getPendingScheduledPublishAt } from '@event-editor/utils';
import {
  TimeOffsetRelation,
  TimeOffsetUnit,
  REMINDERS_GUEST_LIST,
  normalizeTimeOffsetUnit
} from 'constants/enums';
import { queryClient } from '@api/queryClient';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import {
  emailRemindersApi,
  UpsertEmailReminderInput,
  EmailReminder,
  DeleteEmailReminderInput
} from '@event-editor/api/reminder/emailReminder';
import {
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from '@epoch-ui/components/Sheet/sheet';
import { Button } from '@epoch-ui/components/Button';
import { Icon } from '@epoch-ui/components/Icon';
import { ToasterVariant, useToaster } from 'hooks/useToaster';
import { guessTimezone } from 'utils/dateTimeUtils';
import { convertSendAtToFormValue } from 'pages/dashboard/overview/events/schemas/commonUtils';
import { getDefaultPostEventReminderSendAtFormValue } from '@event-editor/tabs/EditEventCommunications/utils/postEventReminderWindow';

import { ReminderScheduler } from '../ReminderScheduler';

import {
  EmailReminderFormInput,
  EmailReminderFormValues,
  GUEST_STATUS_MAPPING,
  emailReminderFormSchema,
  guestStatusOptions
} from './reminder.schema';
/**
 * @param onClose - Callback to handle closing the form
 * @param onDirtyChange - Callback to notify parent of form dirty state changes
 */
interface EmailReminderFormProps {
  onSuccess?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  emailReminder?: EmailReminder;
  onBack?: () => void;
}

export const EmailReminderForm: React.FC<EmailReminderFormProps> = ({
  onSuccess,
  onDirtyChange,
  emailReminder,
  onBack
}) => {
  const event = useEditableEvent();
  const { showToast } = useToaster();

  const isCreate = !emailReminder;

  const mappedGuestStatuses = useMemo(() => {
    if (isCreate) return [];

    return (emailReminder?.selectedGuestStatuses ?? []).map((statusNumber: number) => {
      const statusKey = Object.keys(REMINDERS_GUEST_LIST).find(
        (key) => REMINDERS_GUEST_LIST[key as keyof typeof REMINDERS_GUEST_LIST] === statusNumber
      );
      const guestStatusMapping =
        GUEST_STATUS_MAPPING[statusKey as keyof typeof GUEST_STATUS_MAPPING];
      return guestStatusMapping;
    });
  }, [emailReminder, isCreate]);

  const pendingScheduledPublishAt = getPendingScheduledPublishAt(event.publishSchedule);
  const eventTimezone = event.timezoneCode ?? guessTimezone();
  const defaultPostEventSendAt = useMemo(
    () =>
      event.hasEnded
        ? getDefaultPostEventReminderSendAtFormValue({
            datetimeEnd: event.datetimeEnd,
            timezoneCode: eventTimezone
          })
        : undefined,
    [event.datetimeEnd, event.hasEnded, eventTimezone]
  );

  const form = useForm<EmailReminderFormInput, unknown, EmailReminderFormValues>({
    resolver: zodResolver(
      emailReminderFormSchema(
        event.datetimeScheduled ?? '',
        event.datetimeEnd ?? '',
        eventTimezone,
        pendingScheduledPublishAt
      )
    ),
    values: {
      subject: emailReminder?.subject ?? '',
      message: emailReminder?.message ?? '',
      selectedGuestStatuses: mappedGuestStatuses,
      schedule: {
        quantity: emailReminder?.schedule?.quantity ?? 1,
        unit: normalizeTimeOffsetUnit(emailReminder?.schedule?.unit) ?? TimeOffsetUnit.HOURS,
        relation:
          emailReminder?.schedule?.beforeEventBegins === false
            ? TimeOffsetRelation.AFTER
            : TimeOffsetRelation.BEFORE,
        isRelativeTime: isCreate ? !event.hasEnded : !!emailReminder?.schedule,
        sendAt:
          emailReminder?.datetime && !emailReminder.schedule
            ? convertSendAtToFormValue(emailReminder.datetime, eventTimezone)
            : defaultPostEventSendAt
      }
    }
  });

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyChange?.(form.formState.isDirty);
  }, [form.formState.isDirty, onDirtyChange]);

  const upsertEmailReminderMutation = useMutation({
    mutationFn: (data: UpsertEmailReminderInput) => {
      return emailRemindersApi.upsert({ input: data });
    },
    onSuccess: (data) => {
      if (data.ok) {
        showToast(`Email reminder ${isCreate ? 'created' : 'updated'} successfully`);
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        onSuccess?.();
        return;
      }
      showToast(`Failed to ${isCreate ? 'create' : 'update'} email reminder`, {
        variant: ToasterVariant.ERROR
      });
    },
    onError: (error) => {
      console.error(`Failed to ${isCreate ? 'create' : 'update'} email reminder:`, error);
      showToast(`Failed to ${isCreate ? 'create' : 'update'} email reminder`, {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const deleteEmailReminderMutation = useMutation({
    mutationFn: (input: DeleteEmailReminderInput) => {
      return emailRemindersApi.delete({ input });
    },
    onSuccess: (data) => {
      if (data.ok) {
        showToast('Email reminder deleted successfully');
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        onSuccess?.();
        return;
      }
      showToast('Failed to delete email reminder', {
        variant: ToasterVariant.ERROR
      });
    },
    onError: (error) => {
      console.error('Failed to delete email reminder:', error);
      showToast('Failed to delete email reminder', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const onSave = (data: EmailReminderFormValues): void => {
    /**
     * Transform the form data to the expected format for the backend.
     * Add eventId to the data
     * local schedule.sendAt -> backend 'datetime'
     * local schedule.relation ENUM -> backend 'beforeEventBegins' boolean
     */
    const isRelative = data.schedule.isRelativeTime;
    const transformedData: UpsertEmailReminderInput = {
      eventId: event.id!,
      ...(emailReminder?.id ? { id: emailReminder.id } : {}),
      message: data.message,
      subject: data.subject,
      selectedGuestStatuses: data.selectedGuestStatuses, // TODO: Map properly
      ...(isRelative
        ? {
            schedule: {
              quantity: data.schedule.quantity!,
              unit: data.schedule.unit!,
              beforeEventBegins: data.schedule.relation === TimeOffsetRelation.BEFORE
            }
          }
        : { datetime: data.schedule.sendAt! })
    };
    upsertEmailReminderMutation.mutate(transformedData);
  };

  const onDelete = (): void => {
    if (!emailReminder?.id) return;
    deleteEmailReminderMutation.mutate({ id: emailReminder.id, eventId: event.id! });
  };

  return (
    <>
      {isCreate ? (
        <SheetHeader className="tw-align-start tw-mb-0 tw-flex-shrink-0 tw-p-6 tw-pb-0 ">
          <button
            type="button"
            data-testid="back-button"
            onClick={onBack}
            className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-text-content-tertiary"
          >
            <Icon name="arrow-left" />
            <span className="tw-text-sm tw-font-normal tw-leading-5">Back</span>
          </button>

          <SheetTitle className="tw-mb-0">Create Email reminder</SheetTitle>
          {/* For accessibility */}
          <SheetDescription id="create-reminder-sheet" className="tw-sr-only tw-mb-0">
            Create a reminder for the event
          </SheetDescription>
        </SheetHeader>
      ) : (
        <SheetHeader className="tw-align-start tw-mb-0 tw-flex-shrink-0 tw-p-6 tw-pb-0 ">
          <SheetTitle className="tw-mb-0">Update Email reminder</SheetTitle>
        </SheetHeader>
      )}
      <Form {...form}>
        <form
          id="email-reminder-form"
          onSubmit={form.handleSubmit(onSave, (errors) => {
            console.error(errors);
          })}
          className="tw-flex tw-min-h-0 tw-flex-grow tw-flex-col"
        >
          <div className="tw-flex tw-min-h-0 tw-flex-grow tw-flex-col tw-overflow-y-auto tw-px-6 tw-pb-6">
            <InputField
              name="subject"
              label="Subject"
              placeholder="Subject line for the email"
              description="This will appear in the email subject line"
            />

            <FormField
              control={form.control as any}
              name="message"
              render={({ field, fieldState }) => (
                <FormItem className="tw-mt-4">
                  <FormLabel className="tw-mb-4">Message</FormLabel>
                  <FormControl className="tw-mt-0">
                    <RichTextEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      disabled={false}
                      error={!!fieldState.error?.message}
                      displayImages={false}
                      isDescriptionEmpty={
                        (field.value || '')
                          .replace(/<[^>]*>/g, '')
                          .replace(/&[^;]+;/g, '')
                          .trim().length === 0
                      }
                      showAIModal={false}
                      compactToolbar
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Guest Statuses */}
            <MultiSelectField
              name="selectedGuestStatuses"
              label="Send to guests who are:"
              placeholder="Select guest statuses..."
              options={guestStatusOptions}
            />

            {/* Schedule */}
            <ReminderScheduler />
          </div>
          <div className="tw-flex tw-flex-shrink-0 tw-flex-col">
            <hr className="tw-my-0 tw-w-full tw-border-gray-300 " />
            <SheetFooter className="tw-flex tw-w-full tw-flex-shrink-0 tw-justify-between tw-p-6 ">
              <Button
                type="submit"
                variant="primary"
                className="tw-h-10 tw-rounded-lg tw-p-3"
                loading={upsertEmailReminderMutation.isPending}
                disabled={upsertEmailReminderMutation.isPending}
              >
                {isCreate ? 'Create' : 'Update'}
              </Button>
              {!isCreate && (
                <Button
                  type="button"
                  variant="ghost"
                  className="tw-h-10 tw-rounded-lg tw-bg-transparent tw-p-3 tw-text-negative-foreground"
                  loading={upsertEmailReminderMutation.isPending}
                  disabled={upsertEmailReminderMutation.isPending}
                  onClick={onDelete}
                >
                  <Icon name="trash" className="tw-h-4 tw-w-4 tw-text-negative-foreground" />
                  <span className="tw-ml-1">Delete</span>
                </Button>
              )}
            </SheetFooter>
          </div>
        </form>
      </Form>
    </>
  );
};
